package org.gstechnovos.jexonplastech.sections.about.sections

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.ObjectFit
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.css.TextTransform
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.gstechnovos.jexonplastech.sections.about.styles.ExecutiveCardStyle
import org.gstechnovos.jexonplastech.sections.about.styles.ExecutivesContainerStyle
import org.gstechnovos.jexonplastech.theme.toSitePalette
import org.gstechnovos.jexonplastech.theme.unextractable
import org.gstechnovos.jexonplastech.util.LocalBreakpoint
import org.gstechnovos.jexonplastech.util.Res
import org.gstechnovos.jexonplastech.util.animateSlideUp
import org.gstechnovos.jexonplastech.util.rememberScrollVisibility
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div

// ==========================================
// CMS DATA MODEL
// ==========================================

data class ExecutiveProfile(
    val name: String,
    val title: String,
    val bio: String,
    val imagePath: String
)

data class AboutExecutivesData(
    val overline: String,
    val headline: String,
    val executives: List<ExecutiveProfile>
)

val defaultAboutExecutivesData = AboutExecutivesData(
    overline = "OUR LEADERSHIP",
    headline = "Meet the Team Behind Jexon",
    executives = listOf(
        ExecutiveProfile(
            name = "Jignesh Sagpariya",
            title = "Chief Executive Officer",
            bio = "",
            imagePath = Res.Image.About.Executives.CEO
        ),
        ExecutiveProfile(
            name = "Pragnesh Sagpariya",
            title = "Director of Operations",
            bio = "",
            imagePath = Res.Image.About.Executives.DIRECTOR
        )
    )
)

// ==========================================
// COMPONENT
// ==========================================
/**
 *
 * Images:
 * Executive Headshots: The UI forces a circular shape using a 50% border-radius and hardcodes
 * the size to exactly 150x150px.
 *
 * Constraint: You MUST upload a 1:1 perfect square (e.g., 300x300px or 400x400px) with the
 * person's face centered. The ObjectFit.Cover property will crop the edges to make it a circle.
 *
 * Text:Overline: Renders at 14px in uppercase. Limit to 2–3 words.
 *
 * Headline: Renders at 36px on desktop and 28px on mobile. Limit to 4–7 words.
 *
 * Name: Renders at 24px. Limit to 2–3 words.
 *
 * Title: Renders at 16px. Limit to 2–5 words.
 *
 * Bio: Renders at 16px. Limit to 2–4 sentences so the profile cards don't stretch too far down
 * the page.
 *
 */
@Composable
fun AboutExecutivesSection(data: AboutExecutivesData = defaultAboutExecutivesData) {
    val breakpoint = LocalBreakpoint.current
    val isDesktop = breakpoint >= Res.Breakpoints.LG
    val palette = ColorMode.current.toSitePalette()

    val (isVisible, visibilityRef) = rememberScrollVisibility(threshold = 0.2)

    Column(
        modifier = ExecutivesContainerStyle.toModifier().padding(
            topBottom = if (isDesktop) 100.px else 60.px, leftRight = 5.percent
        ),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Div(
            attrs = Modifier
                .fillMaxWidth()
                .display(DisplayStyle.Flex)
                .flexDirection(FlexDirection.Column)
                .alignItems(com.varabyte.kobweb.compose.css.AlignItems.Center)
                .toAttrs {
                    ref { element ->
                        visibilityRef(element)
                        onDispose {}
                    }
                }
        ) {
            // --- SECTION HEADER ---
            Column(
                modifier = Modifier.maxWidth(800.px).margin(bottom = 60.px).animateSlideUp(isVisible, delayMs = 0),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                SpanText(
                    text = data.overline,
                    modifier = Modifier
                        .color(palette.brand.primaryBlue)
                        .fontSize(14.px)
                        .fontWeight(FontWeight.Bold)
                        .letterSpacing(1.5.px)
                        .textTransform(TextTransform.Uppercase)
                        .margin(bottom = 12.px)
                )

                SpanText(
                    text = data.headline,
                    modifier = Modifier
                        .color(palette.custom.darkText)
                        .fontSize(if (isDesktop) 36.px else 28.px)
                        .fontWeight(FontWeight.Bold)
                        .textAlign(TextAlign.Center)
                )
            }

            // --- EXECUTIVES GRID ---
            SimpleGrid(
                // 1 column mobile, 2 columns desktop (adjust based on number of executives)
                numColumns(1, md = 2),
                modifier = Modifier.fillMaxWidth().maxWidth(1000.px).gap(40.px)
            ) {
                data.executives.forEachIndexed { index, exec ->
                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .height(100.percent)
                            .animateSlideUp(isVisible, delayMs = 150 + (index * 150))
                    ) {
                        ExecutiveCard(exec)
                    }
                }
            }
        }
    }
}

@Composable
private fun ExecutiveCard(profile: ExecutiveProfile) {
    val palette = ColorMode.current.toSitePalette()

    Column(
        modifier = ExecutiveCardStyle.toModifier(),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.Top
    ) {
        // Headshot Image
        Image(
            src = profile.imagePath,
            alt = profile.name,
            modifier = Modifier
                .size(150.px)
                .margin(bottom = 24.px)
                .borderRadius(50.percent) // Makes the image circular
                .objectFit(ObjectFit.Cover)
                .boxShadow(0.px, 5.px, 15.px, color = palette.custom.darkText.toRgb().copyf(alpha = 0.2f))
                .unextractable()
        )

        // Name
        SpanText(
            text = profile.name,
            modifier = Modifier
                .color(palette.custom.darkText)
                .fontSize(24.px)
                .fontWeight(FontWeight.Bold)
                .margin(bottom = 8.px)
                .textAlign(TextAlign.Center)
        )

        // Title
        SpanText(
            text = profile.title,
            modifier = Modifier
                .color(palette.brand.primaryBlue)
                .fontSize(16.px)
                .fontWeight(FontWeight.Medium)
                .margin(bottom = 16.px)
                .textAlign(TextAlign.Center)
        )

        // Bio
        SpanText(
            text = profile.bio,
            modifier = Modifier
                .color(palette.custom.darkText.toRgb().copyf(alpha = 0.7f))
                .fontSize(16.px)
                .lineHeight(1.6)
                .textAlign(TextAlign.Center)
        )
    }
}